"use client";

import { Pause, Play } from "lucide-react";
import { useEffect, useState } from "react";

import type { TrustLogo } from "@/content/trust";
import { cn } from "@/lib/utils";

import { TrustLogoMark } from "./TrustLogoMark";

export type TrustLogoMarqueeLabels = {
  /** Accessible name while the loop runs, e.g. "Pause logos". */
  pause: string;
  /** Accessible name while the loop is paused, e.g. "Play logos". */
  play: string;
};

type TrustLogoMarqueeProps = {
  logos: readonly TrustLogo[];
  labels: TrustLogoMarqueeLabels;
  className?: string;
};

/**
 * The trusted-by band: a slow, seamless CSS loop where motion is allowed, and a
 * static, evenly spread row otherwise (see trust-logos.css). The second list
 * only feeds the loop, so it is hidden from assistive technology.
 *
 * The toggle stops the loop for keyboard and touch users (WCAG 2.2.2). The
 * pause lasts while the page is open. The toggle appears after hydration, so
 * it never shows before it can work, and CSS hides it where motion is off.
 */
export const TrustLogoMarquee = ({
  logos,
  labels,
  className,
}: TrustLogoMarqueeProps) => {
  const [paused, setPaused] = useState(false);
  const [interactive, setInteractive] = useState(false);

  useEffect(() => setInteractive(true), []);

  return (
    <div
      data-trust-band
      data-paused={paused}
      className={cn("trust-marquee", className)}
    >
      <div className="trust-marquee-viewport">
        <div className="trust-marquee-track">
          <ul data-trust-row className="trust-marquee-set">
            {logos.map((logo) => (
              <li key={logo.id} data-trust-logo className="flex items-center">
                <TrustLogoMark logo={logo} />
              </li>
            ))}
          </ul>
          <ul className="trust-marquee-set" aria-hidden="true">
            {logos.map((logo) => (
              <li key={logo.id} className="flex items-center">
                <TrustLogoMark logo={logo} decorative />
              </li>
            ))}
          </ul>
        </div>
      </div>
      {interactive ? (
        <button
          type="button"
          className="trust-marquee-toggle"
          aria-label={paused ? labels.play : labels.pause}
          onClick={() => setPaused((value) => !value)}
        >
          {paused ? (
            <Play aria-hidden className="size-3" strokeWidth={2.25} />
          ) : (
            <Pause aria-hidden className="size-3" strokeWidth={2.25} />
          )}
        </button>
      ) : null}
    </div>
  );
};
